import { getPage, savePage } from '@/api/pages';
import { PageEditorState } from '@/types/page';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

type PageGetter = GetterTree<PageEditorState, RootState>;

const getDefaultState = (): PageEditorState => ({
  id: null,
  sub_title: null,
  strapline: null,
  description: null,
  cta_text: null,
  content: '',
  meta: {
    id: '',
    image_id: null,
    image: null,
    latest_commit: null,
    live_commit: null,
    menu_title: null,
    meta_title: null,
    meta_description: null,
    featured_image_description: null,
    noindex: false,
    canonical_url: null,
    open_graph_title: null,
    open_graph_description: null,
    open_graph_image_crop: null,
    slug: '',
    title: '',
    twitter_title: null,
    twitter_description: null,
    twitter_image_crop: null,
  },
  banner: {
    id: '',
    colour: null,
    background: null,
    icon: null,
  },
  layout_id: null,
  created_at: null,
  updated_at: null,
});

const state: PageEditorState = getDefaultState();

const getters: PageGetter = {
  page: state => state,
};

const actions: ActionTree<PageEditorState, RootState> = {
  async getPage({ commit }, id) {
    commit('setPage', await getPage(id));
  },
  async savePage({ state, commit, dispatch }) {
    try {
      const page = await savePage(state);
      commit('setPage', page);
      dispatch('notifications/success', 'Page saved', { root: true });
    } catch (e) {
      dispatch(
        'notifications/error',
        'There was an error trying to save the page',
        { root: true },
      );
    }
  },
  resetEditor({ commit }) {
    commit('resetPageEditor');
  },
};

const mutations: MutationTree<PageEditorState> = {
  resetPageEditor(pageEditorState: PageEditorState): void {
    Object.assign(pageEditorState, getDefaultState());
  },
  setPage(pageEditorState: PageEditorState, page: PageEditorState): void {
    pageEditorState = Object.assign(pageEditorState, page);
  },
  setPageTitle(pageEditorState: PageEditorState, title: string): void {
    pageEditorState.meta!.title = title;
  },
  setPageSlug(pageEditorState: PageEditorState, slug: string): void {
    pageEditorState.meta.slug = slug;
  },
  setPageSubTitle(pageEditorState: PageEditorState, subTitle: string): void {
    pageEditorState.sub_title = subTitle;
  },
  setPageStrapline(pageEditorState: PageEditorState, strapline: string): void {
    pageEditorState.strapline = strapline;
  },
  setPageDescription(
    pageEditorState: PageEditorState,
    description: string,
  ): void {
    pageEditorState.description = description;
  },
  setPageCTAText(pageEditorState: PageEditorState, CTAText: string): void {
    pageEditorState.cta_text = CTAText;
  },
  setPageContent(pageEditorState: PageEditorState, content: string): void {
    pageEditorState.content = content;
  },
  setPageLayoutId(pageEditorState: PageEditorState, layoutId: string): void {
    pageEditorState.layout_id = layoutId;
  },

  setPageBannerColour(pageEditorState: PageEditorState, colour: string): void {
    pageEditorState.banner.colour = colour;
  },
  setPageBannerBackground(
    pageEditorState: PageEditorState,
    background: string,
  ): void {
    pageEditorState.banner.background = background;
  },
  setPageBannerIcon(pageEditorState: PageEditorState, icon: string): void {
    pageEditorState.banner.icon = icon;
  },

  setPageMetaTitle(pageEditorState: PageEditorState, title: string): void {
    pageEditorState.meta!.meta_title = title;
  },
  setPageMetaDescription(
    pageEditorState: PageEditorState,
    description: string,
  ): void {
    pageEditorState.meta!.meta_description = description;
  },
  setPageCanonicalUrl(
    pageEditorState: PageEditorState,
    canonicalUrl: string,
  ): void {
    pageEditorState.meta!.canonical_url = canonicalUrl;
  },
  setPageTwitterTitle(pageEditorState: PageEditorState, title: string): void {
    pageEditorState.meta!.twitter_title = title;
  },
  setPageTwitterDescription(
    pageEditorState: PageEditorState,
    description: string,
  ): void {
    pageEditorState.meta!.twitter_description = description;
  },
  setPageOpenGraphTitle(pageEditorState: PageEditorState, title: string): void {
    pageEditorState.meta!.open_graph_title = title;
  },
  setPageOpenGraphDescription(
    pageEditorState: PageEditorState,
    description: string,
  ): void {
    pageEditorState.meta!.open_graph_description = description;
  },
};

const pageEditor: Module<PageEditorState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default pageEditor;
